/**
 * Servicios para la estructura organizacional
 * Unión → Asociación → Distrito → Iglesia → Club
 */

import { where, orderBy, Timestamp } from "firebase/firestore";
import { getById, getAll, create, update, remove } from "./base";
import { normalizeName } from "@/lib/utils";
import type {
  Union,
  Association,
  District,
  Church,
  Club,
  Unit,
  Status,
} from "@/types";

/**
 * Genera un código de invitación de 6 caracteres alfanuméricos.
 * Excluye caracteres confusos (I, O, 0, 1).
 * Usa crypto.getRandomValues() para seguridad criptográfica.
 */
export function generateInviteCode(): string {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  const randomBytes = new Uint32Array(6);
  crypto.getRandomValues(randomBytes);
  let code = "";
  for (let i = 0; i < 6; i++) {
    code += chars[randomBytes[i] % chars.length];
  }
  return code;
}

// ==================== UNION ====================

export const unionService = {
  async getById(id: string): Promise<Union | null> {
    return getById<Union>("unions", id);
  },

  async getAll(): Promise<Union[]> {
    return getAll<Union>("unions", [orderBy("name")]);
  },

  async create(
    data: Omit<Union, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Union>("unions", data, userId);
  },

  async update(id: string, data: Partial<Union>): Promise<void> {
    return update<Union>("unions", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("unions", id);
  },
};

// ==================== ASSOCIATION ====================

export const associationService = {
  async getById(id: string): Promise<Association | null> {
    return getById<Association>("associations", id);
  },

  async getAll(): Promise<Association[]> {
    return getAll<Association>("associations", [orderBy("name")]);
  },

  async getByUnion(unionId: string): Promise<Association[]> {
    return getAll<Association>("associations", [
      where("unionId", "==", unionId),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<Association, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Association>("associations", data, userId);
  },

  async update(id: string, data: Partial<Association>): Promise<void> {
    return update<Association>("associations", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("associations", id);
  },
};

// ==================== DISTRICT ====================
/**
 * @deprecated La colección `districts` ya no se usa.
 * Las iglesias se conectan directamente a las asociaciones.
 */
export const districtService = {
  async getById(id: string): Promise<District | null> {
    return getById<District>("districts", id);
  },

  async getAll(): Promise<District[]> {
    return getAll<District>("districts", [orderBy("name")]);
  },

  async getByAssociation(associationId: string): Promise<District[]> {
    return getAll<District>("districts", [
      where("associationId", "==", associationId),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<District, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<District>("districts", data, userId);
  },

  async update(id: string, data: Partial<District>): Promise<void> {
    return update<District>("districts", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("districts", id);
  },
};

// ==================== CHURCH ====================

export const churchService = {
  async getById(id: string): Promise<Church | null> {
    return getById<Church>("churches", id);
  },

  async getAll(): Promise<Church[]> {
    return getAll<Church>("churches", [orderBy("name")]);
  },

  async getByDistrict(districtId: string): Promise<Church[]> {
    return getAll<Church>("churches", [
      where("districtId", "==", districtId),
      orderBy("name"),
    ]);
  },

  async getByAssociation(associationId: string): Promise<Church[]> {
    return getAll<Church>("churches", [
      where("associationId", "==", associationId),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<Church, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Church>("churches", data, userId);
  },

  async update(id: string, data: Partial<Church>): Promise<void> {
    return update<Church>("churches", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("churches", id);
  },
};

// ==================== CLUB ====================

export const clubService = {
  async getById(id: string): Promise<Club | null> {
    return getById<Club>("clubs", id);
  },

  async getAll(): Promise<Club[]> {
    return getAll<Club>("clubs", [orderBy("name")]);
  },

  /** Busca clubs activos cuyo nombre contenga el texto (client-side filter). */
  async searchByName(text: string): Promise<Club[]> {
    const all = await getAll<Club>("clubs", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
    const lower = text.toLowerCase();
    return all.filter((c) => c.name.toLowerCase().includes(lower));
  },

  /**
   * Clubs activos con nombre SIMILAR (insensible a mayúsculas, tildes y
   * espacios extra; match por igualdad o contención en ambos sentidos).
   * Para prevenir duplicados en "Soy Director" y avisar al admin.
   */
  async findSimilarByName(name: string): Promise<Club[]> {
    const norm = (s: string) =>
      (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    const target = norm(name);
    if (target.length < 3) return [];
    const all = await getAll<Club>("clubs", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
    return all.filter((c) => {
      const n = norm(c.name);
      return n === target || n.includes(target) || target.includes(n);
    });
  },

  /**
   * Posibles duplicados de un club que se quiere registrar.
   *
   * La regla real del negocio es **una iglesia = un club** (con varias
   * secciones), así que el nombre NO alcanza: el club de la iglesia Mutis se
   * llama "MUTIS" y alguien pidió registrar "ÁGAPE" en esa misma iglesia sin
   * que saltara ninguna alarma. Aquí se unen los dos criterios:
   *   - nombre similar (mismo criterio que `findSimilarByName`), y
   *   - misma iglesia (churchId, o churchName normalizado como respaldo
   *     porque hay clubes viejos sin churchId).
   */
  async findPossibleDuplicates(params: {
    name?: string;
    churchId?: string;
    churchName?: string;
  }): Promise<Club[]> {
    const norm = (s: string) =>
      (s || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[̀-ͯ]/g, "")
        .replace(/\s+/g, " ")
        .trim();
    const targetName = norm(params.name ?? "");
    const targetChurch = norm(params.churchName ?? "");
    const churchId = (params.churchId ?? "").trim();
    if (targetName.length < 3 && !churchId && targetChurch.length < 3) {
      return [];
    }

    const all = await getAll<Club>("clubs", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);

    return all.filter((c) => {
      if (targetName.length >= 3) {
        const n = norm(c.name);
        if (n === targetName || n.includes(targetName) || targetName.includes(n))
          return true;
      }
      if (churchId && c.churchId && c.churchId === churchId) return true;
      if (targetChurch.length >= 3 && c.churchName) {
        if (norm(c.churchName) === targetChurch) return true;
      }
      return false;
    });
  },

  /** Busca un club por su inviteCode o primeros 6 chars del ID. */
  async getByInviteCode(code: string): Promise<Club | null> {
    const upper = code.toUpperCase();
    // Primero buscar por campo inviteCode
    const byCode = await getAll<Club>("clubs", [
      where("inviteCode", "==", upper),
    ]);
    if (byCode.length > 0) return byCode[0];
    // Fallback: buscar por primeros 6 chars del ID
    const all = await getAll<Club>("clubs", [where("status", "==", "active")]);
    return all.find((c) => c.id.toUpperCase().startsWith(upper)) || null;
  },

  async getByAssociation(associationId: string): Promise<Club[]> {
    return getAll<Club>("clubs", [
      where("associationId", "==", associationId),
      orderBy("name"),
    ]);
  },

  /** @deprecated Iglesia ya no es la relación principal del club */
  async getByChurch(churchId: string): Promise<Club[]> {
    return getAll<Club>("clubs", [
      where("churchId", "==", churchId),
      orderBy("name"),
    ]);
  },

  /** @deprecated Usar sections - mantenido para compatibilidad */
  async getByType(type: Club["type"]): Promise<Club[]> {
    return getAll<Club>("clubs", [where("type", "==", type), orderBy("name")]);
  },

  /** Obtiene clubes que tienen una sección activa de cierto tipo */
  async getByActiveSection(sectionType: string): Promise<Club[]> {
    const all = await getAll<Club>("clubs", [
      where("status", "==", "active"),
      orderBy("name"),
    ]);
    return all.filter((c) => {
      if (c.sections) {
        const section = c.sections[sectionType as keyof typeof c.sections];
        return section?.active;
      }
      // Fallback: legacy clubs con type directo
      return c.type === sectionType;
    });
  },

  async create(
    data: Omit<Club, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    const normalized = {
      ...data,
      ...(typeof data.name === "string"
        ? { name: data.name.trim().toLocaleUpperCase("es") }
        : {}),
      ...(typeof data.directorName === "string"
        ? { directorName: normalizeName(data.directorName) }
        : {}),
    };
    return create<Club>("clubs", normalized, userId);
  },

  async update(id: string, data: Partial<Club>): Promise<void> {
    const normalized: Partial<Club> = {
      ...data,
      ...(typeof data.name === "string"
        ? { name: data.name.trim().toLocaleUpperCase("es") }
        : {}),
      ...(typeof data.directorName === "string"
        ? { directorName: normalizeName(data.directorName) }
        : {}),
    };
    return update<Club>("clubs", id, normalized);
  },

  async delete(id: string): Promise<void> {
    // F1: Soft delete — marcar como inactivo en vez de eliminar
    return update<Club>("clubs", id, { status: "inactive" as Status });
  },

  /**
   * F11 — Disuelve un club fusión SIN dejar a nadie atrapado.
   *
   * Para cada membresía activa de la fusión:
   *   - Si la persona conserva membresía en otro club (el caso normal: la
   *     fusión es aditiva) → la membresía de la fusión pasa a "inactive"
   *     (historial intacto, contadores cuadrados por updateStatus).
   *   - Si la fusión es su ÚNICO club → se TRASLADA a su `originClubId`
   *     (con `memberService.transferToClub`). Sin originClubId ni otro club,
   *     queda reportada en `unresolved` para decisión manual — nunca se borra.
   *
   * Al final el club queda `status: "inactive"` + `dissolvedAt`.
   * Devuelve el resumen para mostrar en la UI.
   */
  async dissolveFusion(
    clubId: string,
    userId: string,
  ): Promise<{
    deactivated: number;
    returned: number;
    unresolved: string[];
    dissolved: boolean;
  }> {
    const { memberService } = await import("./members");
    const club = await getById<Club>("clubs", clubId);
    if (!club) throw new Error("Club no encontrado");
    if (!club.isFusion)
      throw new Error("Este club no está marcado como fusión");

    let deactivated = 0;
    let returned = 0;
    const unresolved: string[] = [];

    // Varias pasadas: si alguien agrega miembros MIENTRAS se disuelve (dos
    // admins a la vez), la relectura los captura y nadie queda activo en un
    // club disuelto. Los `unresolved` no se reintentan (quedaría infinito).
    const seen = new Set<string>();
    for (let pass = 0; pass < 3; pass++) {
      const members = await memberService.getByClub(clubId);
      const active = members.filter(
        (m) => m.status === "active" && !seen.has(m.id),
      );
      if (active.length === 0) break;
      active.forEach((m) => seen.add(m.id));

    for (const m of active) {
      if (!m.personId) {
        unresolved.push(m.personName || m.id);
        continue;
      }
      const others = (await memberService.getByPersonId(m.personId)).filter(
        (o) => o.clubId !== clubId && o.status === "active",
      );
      if (others.length > 0) {
        // Conserva su club real: solo se apaga la membresía de la fusión.
        await memberService.updateStatusWithHistory(
          m.id,
          "inactive",
          `Fusión "${club.name}" disuelta — conserva su membresía en su club de origen`,
          userId,
        );
        deactivated++;
      } else if (m.originClubId) {
        // Validar que el club de origen siga existiendo y activo — si fue
        // renombrado/archivado, devolver ahí sería otro agujero.
        const origin = await getById<Club>("clubs", m.originClubId);
        if (!origin || origin.status !== "active") {
          unresolved.push(m.personName || m.id);
          continue;
        }
        await memberService.transferToClub(
          m.id,
          m.originClubId,
          userId,
          `Retorno por disolución de la fusión "${club.name}"`,
        );
        returned++;
      } else {
        // Se registró directo en la fusión y no sabemos su club real:
        // NO tocar (el error histórico fue borrarlos). Decisión manual.
        unresolved.push(m.personName || m.id);
      }
    }
    }

    // Solo se archiva la fusión cuando NADIE quedó activo dentro. Si hay
    // pendientes, el club sigue activo y la UI lo dice — archivar con gente
    // adentro es exactamente el error que atrapó las 21 fichas del caso COAL.
    const dissolved = unresolved.length === 0;
    if (dissolved) {
      await update<Club>("clubs", clubId, {
        status: "inactive" as Status,
        dissolvedAt: Timestamp.now(),
      });
    }

    return { deactivated, returned, unresolved, dissolved };
  },
};

// ==================== UNIT (Unidad/Patrulla) ====================

export const unitService = {
  async getById(id: string): Promise<Unit | null> {
    return getById<Unit>("units", id);
  },

  async getByClub(clubId: string): Promise<Unit[]> {
    return getAll<Unit>("units", [
      where("clubId", "==", clubId),
      orderBy("name"),
    ]);
  },

  async create(
    data: Omit<Unit, "id" | "createdAt" | "updatedAt">,
    userId: string,
  ): Promise<string> {
    return create<Unit>("units", data, userId);
  },

  async update(id: string, data: Partial<Unit>): Promise<void> {
    return update<Unit>("units", id, data);
  },

  async delete(id: string): Promise<void> {
    return remove("units", id);
  },
};
